import { useState, useEffect, useCallback } from 'react';
import { Mail, Send, Inbox, RefreshCw, ArrowLeft, Bot, Loader2, Check, AlertCircle, ChevronDown, Trash2, Search, BookOpen, MessageSquare, Plus, X, Sparkles, GraduationCap } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const ACCOUNT_COLORS = {
  restaurant: '#C19C7B',
  traiteur: '#34A853',
  perso: '#4285F4',
};

export default function EmailManager() {
  const [accounts, setAccounts] = useState([]);
  const [activeAccount, setActiveAccount] = useState('restaurant');
  const [emails, setEmails] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('inbox'); // inbox, sent, read, compose, knowledge, conversations
  const [aiDraft, setAiDraft] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiInstructions, setAiInstructions] = useState('');
  const [composeData, setComposeData] = useState({ to: '', subject: '', body: '' });
  const [sending, setSending] = useState(false);
  const [stats, setStats] = useState([]);
  const [showAccounts, setShowAccounts] = useState(false);
  const [error, setError] = useState('');
  const [sentEmails, setSentEmails] = useState([]);

  const loadStats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/emails/stats`);
      if (res.ok) setStats(await res.json());
    } catch {}
  }, []);

  const loadInbox = useCallback(async (account) => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/emails/${account}/inbox?limit=25`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setError(err.detail || 'Erreur connexion email');
        setEmails([]);
      } else {
        setEmails(await res.json());
      }
    } catch (e) {
      setError('Erreur de connexion au serveur');
      setEmails([]);
    }
    setLoading(false);
  }, []);

  const loadSent = useCallback(async (account) => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/emails/${account}/sent?limit=25`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setError(err.detail || 'Erreur chargement envoyés');
        setSentEmails([]);
      } else {
        setSentEmails(await res.json());
      }
    } catch {
      setError('Erreur de connexion');
      setSentEmails([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => {
    if (view === 'inbox') loadInbox(activeAccount);
    if (view === 'sent') loadSent(activeAccount);
  }, [activeAccount, view, loadInbox, loadSent]);

  const openEmail = async (em) => {
    setSelectedEmail(em);
    setView('read');
    setAiDraft('');
    setAiInstructions('');
  };

  const generateAIReply = async () => {
    if (!selectedEmail) return;
    setAiLoading(true);
    try {
      const res = await fetch(`${API}/api/emails/ai-reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          account: activeAccount,
          email_id: selectedEmail.id,
          instructions: aiInstructions || undefined,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setAiDraft(data.draft);
        setComposeData({ to: data.to, subject: data.subject, body: data.draft });
      } else {
        setError(data.detail || 'Erreur IA');
      }
    } catch {
      setError('Erreur de connexion');
    }
    setAiLoading(false);
  };

  const sendEmail = async () => {
    if (!composeData.to || !composeData.subject) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/api/emails/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          account: activeAccount,
          to: composeData.to,
          subject: composeData.subject,
          body: composeData.body,
          reply_to_message_id: selectedEmail?.message_id || undefined,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setView('inbox');
        setComposeData({ to: '', subject: '', body: '' });
        setAiDraft('');
      } else {
        setError(data.detail || 'Erreur envoi');
      }
    } catch {
      setError('Erreur de connexion');
    }
    setSending(false);
  };

  const startCompose = () => {
    setSelectedEmail(null);
    setComposeData({ to: '', subject: '', body: '' });
    setAiDraft('');
    setView('compose');
  };

  const deleteEmail = async (emailId, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm('Supprimer cet email ?')) return;
    try {
      const res = await fetch(`${API}/api/emails/${activeAccount}/message/${emailId}`, { method: 'DELETE' });
      if (res.ok) {
        setEmails(prev => prev.filter(em => em.id !== emailId));
        if (selectedEmail?.id === emailId) setView('inbox');
      } else {
        const err = await res.json().catch(() => ({}));
        setError(err.detail || 'Erreur suppression');
      }
    } catch {
      setError('Erreur de connexion');
    }
  };

  const useAsDraft = () => {
    setView('compose');
  };

  const acctColor = ACCOUNT_COLORS[activeAccount] || '#666';
  const acctStat = stats.find(s => s.id === activeAccount);

  return (
    <div className="space-y-4" data-testid="email-manager-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[10px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))] mb-0.5">Messagerie</p>
          <h1 className="text-base font-semibold text-[rgb(var(--lm-text))]">Emails</h1>
        </div>
        <div className="flex gap-1.5">
          <Button size="sm" variant="outline" onClick={() => { if (view === 'sent') loadSent(activeAccount); else loadInbox(activeAccount); }} className="text-[10px] border-[rgb(var(--lm-border))] h-8" data-testid="refresh-inbox-btn">
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
          </Button>
          <Button size="sm" onClick={startCompose} className="text-[10px] text-white h-8 gap-1" style={{ background: acctColor }} data-testid="compose-btn">
            <Send size={12} /> Nouveau
          </Button>
        </div>
      </div>

      {/* Account Stats Bar */}
      {stats.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {stats.map(s => {
            const isActive = activeAccount === s.id;
            const color = ACCOUNT_COLORS[s.id];
            return (
              <button key={s.id} onClick={() => { setActiveAccount(s.id); if (view !== 'compose' && view !== 'read') setView('inbox'); }}
                className={`relative p-3 rounded-xl border transition-all text-left ${
                  isActive ? 'border-current shadow-sm' : 'border-[rgb(var(--lm-border))]/30 hover:border-[rgb(var(--lm-border))]'
                }`}
                style={isActive ? { borderColor: color, background: color + '08' } : {}}
                data-testid={`account-tab-${s.id}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-[11px] font-medium ${isActive ? 'text-[rgb(var(--lm-text))]' : 'text-[rgb(var(--lm-text-secondary))]'}`}>
                    {ACCOUNTS_UI[s.id]?.label}
                  </span>
                  {s.status === 'connected' ? (
                    <div className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
                  ) : (
                    <AlertCircle size={10} className="text-red-400" />
                  )}
                </div>
                <p className="text-[9px] text-[rgb(var(--lm-text-muted))] truncate">{s.email}</p>
                {s.status === 'connected' && (
                  <div className="flex gap-2 mt-1.5">
                    {s.unread > 0 && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full text-white font-medium" style={{ background: color }}>{s.unread} non lu{s.unread > 1 ? 's' : ''}</span>
                    )}
                    <span className="text-[9px] text-[rgb(var(--lm-text-muted))]">{s.total} total</span>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div className="flex items-center gap-2 p-2.5 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700" data-testid="email-error">
          <AlertCircle size={14} />
          {error}
          <button onClick={() => setError('')} className="ml-auto text-red-400 hover:text-red-600">x</button>
        </div>
      )}

      {/* Folder Tabs (Inbox / Sent / Conversations / IA) */}
      {(view === 'inbox' || view === 'sent' || view === 'conversations' || view === 'knowledge') && (
        <div className="flex gap-1 p-0.5 bg-[rgb(var(--lm-card))] rounded-lg w-fit" data-testid="folder-tabs">
          <button onClick={() => setView('inbox')}
            className={`px-3 py-1.5 rounded-md text-[11px] font-medium transition-all flex items-center gap-1.5 ${
              view === 'inbox' ? 'bg-[rgb(var(--lm-bg))] text-[rgb(var(--lm-text))] shadow-sm' : 'text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text-secondary))]'
            }`} data-testid="tab-inbox">
            <Inbox size={12} /> Reception
          </button>
          <button onClick={() => setView('sent')}
            className={`px-3 py-1.5 rounded-md text-[11px] font-medium transition-all flex items-center gap-1.5 ${
              view === 'sent' ? 'bg-[rgb(var(--lm-bg))] text-[rgb(var(--lm-text))] shadow-sm' : 'text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text-secondary))]'
            }`} data-testid="tab-sent">
            <Send size={12} /> Envoyes
          </button>
          <button onClick={() => setView('conversations')}
            className={`px-3 py-1.5 rounded-md text-[11px] font-medium transition-all flex items-center gap-1.5 ${
              view === 'conversations' ? 'bg-[rgb(var(--lm-bg))] text-[rgb(var(--lm-text))] shadow-sm' : 'text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text-secondary))]'
            }`} data-testid="tab-conversations">
            <MessageSquare size={12} /> Conversations
          </button>
          <button onClick={() => setView('knowledge')}
            className={`px-3 py-1.5 rounded-md text-[11px] font-medium transition-all flex items-center gap-1.5 ${
              view === 'knowledge' ? 'bg-[rgb(var(--lm-bg))] text-[rgb(var(--lm-text))] shadow-sm' : 'text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text-secondary))]'
            }`} data-testid="tab-knowledge">
            <GraduationCap size={12} /> Entrainer IA
          </button>
        </div>
      )}

      {/* Inbox View */}
      {view === 'inbox' && (
        <div className="bg-[rgb(var(--lm-card))] rounded-lg border border-[rgb(var(--lm-border))]/30 divide-y divide-[rgb(var(--lm-border))]/20" data-testid="inbox-list">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={20} className="animate-spin text-[rgb(var(--lm-text-muted))]" />
            </div>
          ) : emails.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Inbox size={28} className="text-[rgb(var(--lm-text-muted))] mb-2" />
              <p className="text-xs text-[rgb(var(--lm-text-muted))]">{error ? 'Impossible de charger' : 'Aucun email'}</p>
            </div>
          ) : (
            emails.map((em) => (
              <div key={em.id} className="flex items-center group">
                <button onClick={() => openEmail(em)}
                  className={`flex-1 text-left px-3 py-2.5 hover:bg-[rgb(var(--lm-bg))]/50 transition-colors ${!em.is_read ? 'bg-[rgb(var(--lm-bg))]/30' : ''}`}
                  data-testid={`email-item-${em.id}`}
                >
                  <div className="flex items-start gap-2">
                    {!em.is_read && <div className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0" style={{ background: acctColor }} />}
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-baseline gap-2">
                        <span className={`text-xs truncate ${!em.is_read ? 'font-semibold text-[rgb(var(--lm-text))]' : 'text-[rgb(var(--lm-text-secondary))]'}`}>
                          {em.from_name}
                        </span>
                        <span className="text-[9px] text-[rgb(var(--lm-text-muted))] shrink-0">{em.date?.split(',')[0]}</span>
                      </div>
                      <p className={`text-[11px] truncate ${!em.is_read ? 'font-medium text-[rgb(var(--lm-text))]' : 'text-[rgb(var(--lm-text-secondary))]'}`}>
                        {em.subject}
                      </p>
                      <p className="text-[10px] text-[rgb(var(--lm-text-muted))] truncate mt-0.5">{em.body?.slice(0, 100)}</p>
                    </div>
                  </div>
                </button>
                <button
                  onClick={(e) => deleteEmail(em.id, e)}
                  className="px-2 py-2 opacity-0 group-hover:opacity-100 transition-opacity text-[rgb(var(--lm-text-muted))] hover:text-red-500"
                  title="Supprimer"
                  data-testid={`delete-email-${em.id}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))
          )}
        </div>
      )}

      {/* Sent View */}
      {view === 'sent' && (
        <div className="bg-[rgb(var(--lm-card))] rounded-lg border border-[rgb(var(--lm-border))]/30 divide-y divide-[rgb(var(--lm-border))]/20" data-testid="sent-list">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={20} className="animate-spin text-[rgb(var(--lm-text-muted))]" />
            </div>
          ) : sentEmails.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Send size={28} className="text-[rgb(var(--lm-text-muted))] mb-2" />
              <p className="text-xs text-[rgb(var(--lm-text-muted))]">Aucun email envoye</p>
            </div>
          ) : (
            sentEmails.map((em) => (
              <button key={em.id}
                onClick={() => { setSelectedEmail({ ...em, from_name: 'Moi', from_email: em.to_email, is_read: true }); setView('read'); }}
                className="flex-1 w-full text-left px-3 py-2.5 hover:bg-[rgb(var(--lm-bg))]/50 transition-colors"
                data-testid={`sent-item-${em.id}`}
              >
                <div className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 bg-[rgb(var(--lm-text-muted))]/40" />
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-baseline gap-2">
                      <span className="text-xs text-[rgb(var(--lm-text-secondary))] truncate">
                        A: {em.to_name || em.to_email}
                      </span>
                      <span className="text-[9px] text-[rgb(var(--lm-text-muted))] shrink-0">{em.date?.split(',')[0]}</span>
                    </div>
                    <p className="text-[11px] text-[rgb(var(--lm-text-secondary))] truncate">{em.subject}</p>
                    <p className="text-[10px] text-[rgb(var(--lm-text-muted))] truncate mt-0.5">{em.body?.slice(0, 100)}</p>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      )}

      {/* Read Email View */}
      {view === 'read' && selectedEmail && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <button onClick={() => setView('inbox')} className="flex items-center gap-1 text-xs text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text))]" data-testid="back-to-inbox">
              <ArrowLeft size={14} /> Retour
            </button>
            <button onClick={(e) => deleteEmail(selectedEmail.id, e)}
              className="flex items-center gap-1 text-xs text-red-400 hover:text-red-600 transition-colors"
              data-testid="delete-current-email"
            >
              <Trash2 size={14} /> Supprimer
            </button>
          </div>

          <div className="bg-[rgb(var(--lm-card))] rounded-lg border border-[rgb(var(--lm-border))]/30 p-4">
            <h2 className="text-sm font-semibold text-[rgb(var(--lm-text))] mb-2">{selectedEmail.subject}</h2>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs font-medium text-[rgb(var(--lm-text-secondary))]">{selectedEmail.from_name}</span>
              <span className="text-[10px] text-[rgb(var(--lm-text-muted))]">&lt;{selectedEmail.from_email}&gt;</span>
              <span className="text-[9px] text-[rgb(var(--lm-text-muted))] ml-auto">{selectedEmail.date}</span>
            </div>
            <div className="text-xs text-[rgb(var(--lm-text-secondary))] leading-relaxed whitespace-pre-wrap border-t border-[rgb(var(--lm-border))]/20 pt-3">
              {selectedEmail.full_body || selectedEmail.body}
            </div>
          </div>

          {/* AI Reply Section */}
          <div className="bg-[rgb(var(--lm-card))] rounded-lg border border-[rgb(var(--lm-border))]/30 p-4">
            <div className="flex items-center gap-2 mb-3">
              <Bot size={14} style={{ color: acctColor }} />
              <span className="text-xs font-medium text-[rgb(var(--lm-text))]">Reponse IA</span>
            </div>

            <div className="flex gap-2 mb-3">
              <Input
                value={aiInstructions}
                onChange={e => setAiInstructions(e.target.value)}
                placeholder="Instructions (ex: refuse poliment, propose un autre jour...)"
                className="text-xs bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))]"
                data-testid="ai-instructions-input"
              />
              <Button size="sm" onClick={generateAIReply} disabled={aiLoading}
                className="text-[10px] text-white shrink-0" style={{ background: acctColor }}
                data-testid="generate-ai-reply-btn"
              >
                {aiLoading ? <Loader2 size={12} className="animate-spin" /> : <Bot size={12} />}
                <span className="ml-1">{aiLoading ? 'Redaction...' : 'Generer'}</span>
              </Button>
            </div>

            {aiDraft && (
              <div className="space-y-2">
                <div className="bg-[rgb(var(--lm-bg))] rounded-lg p-3 text-xs text-[rgb(var(--lm-text-secondary))] leading-relaxed whitespace-pre-wrap" data-testid="ai-draft">
                  {aiDraft}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={useAsDraft} className="text-[10px] text-white" style={{ background: acctColor }} data-testid="use-draft-btn">
                    <Send size={12} className="mr-1" /> Utiliser et envoyer
                  </Button>
                  <Button size="sm" variant="outline" onClick={generateAIReply} className="text-[10px] border-[rgb(var(--lm-border))]" data-testid="regenerate-btn">
                    Regenerer
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Compose View */}
      {view === 'compose' && (
        <div className="space-y-3">
          <button onClick={() => setView(selectedEmail ? 'read' : 'inbox')} className="flex items-center gap-1 text-xs text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text))]" data-testid="back-from-compose">
            <ArrowLeft size={14} /> Retour
          </button>

          <div className="bg-[rgb(var(--lm-card))] rounded-lg border border-[rgb(var(--lm-border))]/30 p-4 space-y-3">
            <div className="flex items-center gap-2 text-[10px] text-[rgb(var(--lm-text-muted))]">
              <span>De:</span>
              <span className="font-medium" style={{ color: acctColor }}>{ACCOUNTS_UI[activeAccount]?.email}</span>
            </div>
            <Input
              value={composeData.to}
              onChange={e => setComposeData(p => ({ ...p, to: e.target.value }))}
              placeholder="Destinataire"
              className="text-xs bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))]"
              data-testid="compose-to"
            />
            <Input
              value={composeData.subject}
              onChange={e => setComposeData(p => ({ ...p, subject: e.target.value }))}
              placeholder="Objet"
              className="text-xs bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))]"
              data-testid="compose-subject"
            />
            <textarea
              value={composeData.body}
              onChange={e => setComposeData(p => ({ ...p, body: e.target.value }))}
              placeholder="Votre message..."
              rows={10}
              className="w-full text-xs bg-[rgb(var(--lm-bg))] border border-[rgb(var(--lm-border))] rounded-lg px-3 py-2 text-[rgb(var(--lm-text))] placeholder:text-[rgb(var(--lm-text-muted))] focus:outline-none resize-none"
              data-testid="compose-body"
            />
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="outline" onClick={() => setView('inbox')} className="text-[10px] border-[rgb(var(--lm-border))]">
                Annuler
              </Button>
              <Button size="sm" onClick={sendEmail} disabled={sending || !composeData.to || !composeData.subject}
                className="text-[10px] text-white" style={{ background: acctColor }}
                data-testid="send-email-btn"
              >
                {sending ? <Loader2 size={12} className="animate-spin mr-1" /> : <Send size={12} className="mr-1" />}
                {sending ? 'Envoi...' : 'Envoyer'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Account Status - now integrated in account tabs above */}

      {/* Conversations View */}
      {view === 'conversations' && <ConversationsView api={API} />}

      {/* AI Knowledge View */}
      {view === 'knowledge' && <AIKnowledgeView api={API} />}
    </div>
  );
}

const ACCOUNTS_UI = {
  restaurant: { label: 'Restaurant', email: 'contact@restaurant-le-mas.fr' },
  traiteur: { label: 'Traiteur', email: 'contact@traiteur-du-mas.fr' },
  perso: { label: 'Personnel', email: 'sanmartinlaurent@gmail.com' },
};

/* ── Conversations View ── */
function ConversationsView({ api }) {
  const [convos, setConvos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);
  const [thread, setThread] = useState([]);
  const [threadLoading, setThreadLoading] = useState(false);

  useEffect(() => {
    fetch(`${api}/api/emails/conversations?limit=50`)
      .then(r => r.json())
      .then(d => setConvos(Array.isArray(d) ? d : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [api]);

  const openThread = async (contact) => {
    setSelectedContact(contact);
    setThreadLoading(true);
    try {
      const r = await fetch(`${api}/api/emails/conversation/${encodeURIComponent(contact)}`);
      const d = await r.json();
      setThread(d.emails || []);
    } catch { setThread([]); }
    setThreadLoading(false);
  };

  if (selectedContact) {
    return (
      <div className="space-y-3" data-testid="conversation-thread">
        <button onClick={() => setSelectedContact(null)} className="flex items-center gap-1 text-xs text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text))]">
          <ArrowLeft size={14} /> Retour
        </button>
        <div className="flex items-center gap-2 mb-2">
          <MessageSquare size={14} className="text-blue-400" />
          <span className="text-xs font-medium text-[rgb(var(--lm-text))]">{selectedContact}</span>
          <span className="text-[9px] text-[rgb(var(--lm-text-muted))]">{thread.length} email(s)</span>
        </div>
        {threadLoading ? (
          <div className="flex justify-center py-8"><Loader2 size={16} className="animate-spin text-[rgb(var(--lm-text-muted))]" /></div>
        ) : (
          <div className="space-y-2">
            {thread.map((em, i) => (
              <div key={i} className="bg-[rgb(var(--lm-card))] rounded-lg border border-[rgb(var(--lm-border))]/30 p-3" data-testid={`thread-email-${i}`}>
                <div className="flex items-baseline justify-between mb-1">
                  <span className="text-[10px] font-medium text-[rgb(var(--lm-text))]">{em.subject}</span>
                  <span className="text-[9px] text-[rgb(var(--lm-text-muted))]">{em.sent_at?.slice(0, 10)}</span>
                </div>
                <p className="text-[10px] text-[rgb(var(--lm-text-secondary))] leading-relaxed whitespace-pre-wrap line-clamp-4">{em.body?.slice(0, 300)}</p>
                <p className="text-[9px] text-[rgb(var(--lm-text-muted))] mt-1">via {em.account}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-[rgb(var(--lm-card))] rounded-lg border border-[rgb(var(--lm-border))]/30 divide-y divide-[rgb(var(--lm-border))]/20" data-testid="conversations-list">
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 size={16} className="animate-spin text-[rgb(var(--lm-text-muted))]" /></div>
      ) : convos.length === 0 ? (
        <div className="text-center py-12">
          <MessageSquare size={24} className="text-[rgb(var(--lm-text-muted))] mx-auto mb-2" />
          <p className="text-xs text-[rgb(var(--lm-text-muted))]">Aucune conversation</p>
        </div>
      ) : convos.map((c, i) => (
        <button key={i} onClick={() => openThread(c.contact)} className="w-full text-left px-3 py-2.5 hover:bg-[rgb(var(--lm-bg))]/50 transition-colors" data-testid={`convo-${i}`}>
          <div className="flex items-start gap-2">
            <div className="w-7 h-7 rounded-full bg-blue-500/10 flex items-center justify-center shrink-0 mt-0.5">
              <Mail size={12} className="text-blue-500" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex justify-between items-baseline gap-2">
                <span className="text-xs font-medium text-[rgb(var(--lm-text))] truncate">{c.contact}</span>
                <span className="text-[9px] text-[rgb(var(--lm-text-muted))] shrink-0">{c.last_date?.slice(0, 10)}</span>
              </div>
              <p className="text-[10px] text-[rgb(var(--lm-text-secondary))] truncate">{c.last_subject}</p>
              <span className="text-[9px] text-[rgb(var(--lm-text-muted))]">{c.count} email(s) envoye(s)</span>
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

/* ── AI Knowledge Base View ── */
const KNOWLEDGE_CATEGORIES = [
  { id: 'faq', label: 'FAQ', color: '#3B82F6', desc: 'Questions frequentes (horaires, capacite, menus...)' },
  { id: 'style', label: 'Style & Ton', color: '#8B5CF6', desc: 'Comment l\'IA doit communiquer en votre nom' },
  { id: 'reponses_types', label: 'Reponses types', color: '#059669', desc: 'Modeles de reponses pour les situations courantes' },
  { id: 'style_appris', label: 'Appris auto', color: '#F59E0B', desc: 'Styles extraits automatiquement de vos emails' },
];

function AIKnowledgeView({ api }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ category: 'faq', title: '', content: '' });
  const [saving, setSaving] = useState(false);
  const [filterCat, setFilterCat] = useState('all');

  const load = useCallback(() => {
    fetch(`${api}/api/emails/ai-knowledge`)
      .then(r => r.json())
      .then(d => setEntries(Array.isArray(d) ? d : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const addEntry = async () => {
    if (!form.title.trim() || !form.content.trim()) { toast.error('Titre et contenu requis'); return; }
    setSaving(true);
    try {
      const r = await fetch(`${api}/api/emails/ai-knowledge`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (r.ok) {
        toast.success('Connaissance ajoutee !');
        setForm({ category: 'faq', title: '', content: '' });
        setShowAdd(false);
        load();
      }
    } catch { toast.error('Erreur'); }
    setSaving(false);
  };

  const removeEntry = async (id) => {
    try {
      await fetch(`${api}/api/emails/ai-knowledge/${id}`, { method: 'DELETE' });
      setEntries(prev => prev.filter(e => e.id !== id));
      toast.success('Supprime');
    } catch { toast.error('Erreur'); }
  };

  const filtered = filterCat === 'all' ? entries : entries.filter(e => e.category === filterCat);
  const catColor = (cat) => KNOWLEDGE_CATEGORIES.find(c => c.id === cat)?.color || '#6B7280';

  return (
    <div className="space-y-4" data-testid="ai-knowledge-view">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <GraduationCap size={16} className="text-purple-400" />
          <div>
            <p className="text-xs font-medium text-[rgb(var(--lm-text))]">Base de connaissances IA</p>
            <p className="text-[9px] text-[rgb(var(--lm-text-muted))]">{entries.length} entree(s) — L'IA utilise ces infos pour personnaliser ses reponses</p>
          </div>
        </div>
        <Button size="sm" onClick={() => setShowAdd(!showAdd)} className="text-[10px] bg-purple-500 text-white h-7" data-testid="add-knowledge-btn">
          <Plus size={12} className="mr-1" /> Ajouter
        </Button>
      </div>

      {/* Add form */}
      {showAdd && (
        <div className="bg-[rgb(var(--lm-card))] rounded-lg border border-purple-500/20 p-4 space-y-3" data-testid="add-knowledge-form">
          <div className="grid grid-cols-3 gap-2">
            {KNOWLEDGE_CATEGORIES.filter(c => c.id !== 'style_appris').map(cat => (
              <button key={cat.id} onClick={() => setForm(f => ({ ...f, category: cat.id }))}
                className={`p-2 rounded-lg border text-left transition-all ${form.category === cat.id ? 'border-current shadow-sm' : 'border-[rgb(var(--lm-border))]/30'}`}
                style={form.category === cat.id ? { borderColor: cat.color, background: cat.color + '08' } : {}}
                data-testid={`cat-btn-${cat.id}`}>
                <p className="text-[10px] font-medium" style={{ color: cat.color }}>{cat.label}</p>
                <p className="text-[8px] text-[rgb(var(--lm-text-muted))] leading-snug mt-0.5">{cat.desc}</p>
              </button>
            ))}
          </div>
          <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
            placeholder="Titre (ex: Horaires d'ouverture)" className="text-xs bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))]" data-testid="knowledge-title" />
          <textarea value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
            placeholder="Contenu que l'IA doit connaitre..." rows={4}
            className="w-full text-xs bg-[rgb(var(--lm-bg))] border border-[rgb(var(--lm-border))] rounded-lg px-3 py-2 text-[rgb(var(--lm-text))] placeholder:text-[rgb(var(--lm-text-muted))] focus:outline-none resize-none" data-testid="knowledge-content" />
          <div className="flex gap-2">
            <Button size="sm" onClick={addEntry} disabled={saving} className="text-[10px] bg-purple-500 text-white h-7" data-testid="save-knowledge-btn">
              {saving ? <Loader2 size={12} className="animate-spin mr-1" /> : <Check size={12} className="mr-1" />}
              Enregistrer
            </Button>
            <Button size="sm" variant="outline" onClick={() => setShowAdd(false)} className="text-[10px] h-7 border-[rgb(var(--lm-border))]">Annuler</Button>
          </div>
        </div>
      )}

      {/* Filter pills */}
      <div className="flex gap-1.5 flex-wrap">
        <button onClick={() => setFilterCat('all')}
          className={`px-2.5 py-1 rounded-full text-[10px] font-medium transition-all ${filterCat === 'all' ? 'bg-[rgb(var(--lm-text))] text-[rgb(var(--lm-bg))]' : 'bg-[rgb(var(--lm-card))] text-[rgb(var(--lm-text-muted))] border border-[rgb(var(--lm-border))]/30'}`}>
          Tout ({entries.length})
        </button>
        {KNOWLEDGE_CATEGORIES.map(cat => {
          const count = entries.filter(e => e.category === cat.id).length;
          return (
            <button key={cat.id} onClick={() => setFilterCat(cat.id)}
              className={`px-2.5 py-1 rounded-full text-[10px] font-medium transition-all ${filterCat === cat.id ? 'text-white' : 'border border-[rgb(var(--lm-border))]/30'}`}
              style={filterCat === cat.id ? { background: cat.color } : { color: cat.color }}
              data-testid={`filter-${cat.id}`}>
              {cat.label} ({count})
            </button>
          );
        })}
      </div>

      {/* Entries list */}
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 size={16} className="animate-spin text-[rgb(var(--lm-text-muted))]" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-8">
          <BookOpen size={24} className="text-[rgb(var(--lm-text-muted))] mx-auto mb-2" />
          <p className="text-xs text-[rgb(var(--lm-text-muted))]">Aucune connaissance. Ajoutez des FAQ, votre style, des reponses types...</p>
          <p className="text-[9px] text-[rgb(var(--lm-text-muted))] mt-1">Plus l'IA a de contexte, meilleures seront ses reponses !</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(entry => (
            <div key={entry.id} className="bg-[rgb(var(--lm-card))] rounded-lg border border-[rgb(var(--lm-border))]/30 p-3 group" data-testid={`knowledge-entry-${entry.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="px-1.5 py-0.5 rounded-full text-[8px] font-bold text-white" style={{ background: catColor(entry.category) }}>
                      {KNOWLEDGE_CATEGORIES.find(c => c.id === entry.category)?.label || entry.category}
                    </span>
                    <span className="text-xs font-medium text-[rgb(var(--lm-text))]">{entry.title}</span>
                  </div>
                  <p className="text-[10px] text-[rgb(var(--lm-text-secondary))] leading-relaxed whitespace-pre-wrap">{entry.content}</p>
                  <p className="text-[8px] text-[rgb(var(--lm-text-muted))] mt-1">{entry.created_at?.slice(0, 10)}</p>
                </div>
                <button onClick={() => removeEntry(entry.id)}
                  className="p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-red-500/10 text-red-400 transition-all shrink-0"
                  data-testid={`delete-knowledge-${entry.id}`}>
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
